import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellRing, Send, Cake, ClipboardCheck, Moon, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const supported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const hourLabel = (h) => `${h % 12 || 12}:00 ${h < 12 ? "AM" : "PM"}`;
const HOURS = Array.from({ length: 24 }, (_, h) => h);

const HourSelect = ({ value, onChange, testid }) => (
  <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
    <SelectTrigger className="h-8 w-[110px] border-zinc-800 bg-zinc-950 text-xs focus:ring-[#E0575B]/70" data-testid={testid}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="border-zinc-800 bg-zinc-950">
      {HOURS.map((h) => (
        <SelectItem key={h} value={String(h)} data-testid={`${testid}-${h}`}>{hourLabel(h)}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const HOURS_AFTER = Array.from({ length: 6 }, (_, i) => i + 7); // 7–12 hrs

const HoursAfterSelect = ({ value, onChange, testid }) => (
  <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
    <SelectTrigger className="h-8 w-[84px] border-zinc-800 bg-zinc-950 text-xs focus:ring-[#E0575B]/70" data-testid={testid}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="border-zinc-800 bg-zinc-950">
      {HOURS_AFTER.map((h) => (
        <SelectItem key={h} value={String(h)} data-testid={`${testid}-${h}`}>{h} hrs</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const PrefRow = ({ icon: Icon, tone, title, desc, checked, onToggle, testid }) => (
  <label className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid={testid}>
    <span className="flex min-w-0 items-start gap-3">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
      <span className="min-w-0">
        <span className="block text-sm font-600 text-zinc-100">{title}</span>
        <span className="block text-xs text-zinc-400">{desc}</span>
      </span>
    </span>
    <Switch checked={checked} onCheckedChange={onToggle} data-testid={`${testid}-toggle`} className="data-[state=checked]:bg-[#9B2226]" />
  </label>
);

export function NotificationSettings() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [prefs, setPrefs] = useState({ celebrations: true, post_tour: true, import_alerts: true, quiet_start: 23, quiet_end: 8, nudge1_on: true, nudge1_hours: 8, nudge2_on: true, nudge2_hours: 11, nudge_morning_on: true, nudge_morning_hour: 9 });
  const ok = supported();

  useEffect(() => {
    if (!ok) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setEnabled(!!sub && Notification.permission === "granted"))
      .catch(() => {});
    api.pushPrefs().then(setPrefs).catch(() => {});
  }, [ok]);

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await api.pushSetPrefs(next);
    } catch {
      toast.error("Couldn't save that preference.");
    }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications blocked. Enable them in your device settings and try again.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { key } = await api.pushVapidKey();
      if (!key) {
        toast.error("Push isn't configured on the server.");
        return;
      }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }
      const json = sub.toJSON();
      await api.pushSubscribe({ endpoint: json.endpoint, keys: json.keys });
      setEnabled(true);
      toast.success("Push notifications are on for this device.");
    } catch (e) {
      toast.error("Couldn't turn on notifications. On iPhone, add Stage Door to your Home Screen first.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const json = sub.toJSON();
        await api.pushUnsubscribe({ endpoint: json.endpoint, keys: json.keys || {} });
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Notifications turned off for this device.");
    } catch (e) {
      toast.error("Couldn't turn off notifications.");
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await api.pushTest();
      if (res.sent > 0) toast.success("Test notification sent — check your device.");
      else toast.error("No active devices received it. Try turning notifications on again.");
    } catch (e) {
      toast.error("Couldn't send the test.");
    } finally {
      setTesting(false);
    }
  };

  if (!ok) {
    return (
      <p className="text-xs text-zinc-400" data-testid="push-unsupported">
        Your browser doesn't support notifications. On iPhone, add Stage Door to your Home Screen and open it from there to enable alerts.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="notification-settings">
      {enabled ? (
        <>
          <div className="flex items-center gap-2 text-sm text-emerald-400">
            <BellRing className="h-4 w-4" />
            <span data-testid="push-status-on">Notifications are on for this device.</span>
          </div>

          <div className="space-y-2">
            <PrefRow
              icon={Cake}
              tone="text-amber-400"
              title="Celebrations"
              desc="Birthday & anniversary alerts for repeat guests, at 8 AM ET."
              checked={prefs.celebrations}
              onToggle={(v) => savePrefs({ ...prefs, celebrations: v })}
              testid="pref-celebrations"
            />
            <PrefRow
              icon={UploadCloud}
              tone="text-emerald-400"
              title="Import alerts"
              desc="A heads-up when a Shortcut screenshot imports a tour sheet or a work schedule."
              checked={prefs.import_alerts}
              onToggle={(v) => savePrefs({ ...prefs, import_alerts: v })}
              testid="pref-import-alerts"
            />
            <PrefRow
              icon={ClipboardCheck}
              tone="text-[#E0575B]"
              title="Post-tour reminders"
              desc="Nudges to add notes & mark a tour complete after your shift."
              checked={prefs.post_tour}
              onToggle={(v) => savePrefs({ ...prefs, post_tour: v })}
              testid="pref-post-tour"
            />
          </div>

          {prefs.post_tour && (
            <div className="space-y-2.5 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid="reminder-times">
              <div className="flex items-start gap-3">
                <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                <div className="min-w-0">
                  <span className="block text-sm font-600 text-zinc-100">Reminder times</span>
                  <span className="block text-xs text-zinc-400">Pick which post-tour nudges you get and when (quiet hours still apply).</span>
                </div>
              </div>
              <div className="space-y-2.5 pl-7">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-zinc-300">
                  <Switch checked={prefs.nudge1_on} onCheckedChange={(v) => savePrefs({ ...prefs, nudge1_on: v })} data-testid="nudge1-toggle" className="data-[state=checked]:bg-[#9B2226]" />
                  <span className="whitespace-nowrap">First reminder</span>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <HoursAfterSelect value={prefs.nudge1_hours} onChange={(v) => savePrefs({ ...prefs, nudge1_hours: v })} testid="nudge1-hours" />
                    <span>after the tour</span>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-zinc-300">
                  <Switch checked={prefs.nudge2_on} onCheckedChange={(v) => savePrefs({ ...prefs, nudge2_on: v })} data-testid="nudge2-toggle" className="data-[state=checked]:bg-[#9B2226]" />
                  <span className="whitespace-nowrap">Second reminder</span>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <HoursAfterSelect value={prefs.nudge2_hours} onChange={(v) => savePrefs({ ...prefs, nudge2_hours: v })} testid="nudge2-hours" />
                    <span>after the tour</span>
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs text-zinc-300">
                  <Switch checked={prefs.nudge_morning_on} onCheckedChange={(v) => savePrefs({ ...prefs, nudge_morning_on: v })} data-testid="nudge-morning-toggle" className="data-[state=checked]:bg-[#9B2226]" />
                  <span className="whitespace-nowrap">Next morning at</span>
                  <HourSelect value={prefs.nudge_morning_hour} onChange={(v) => savePrefs({ ...prefs, nudge_morning_hour: v })} testid="nudge-morning-hour" />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid="quiet-hours">
            <div className="flex items-start gap-3">
              <Moon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-400" />
              <div className="min-w-0">
                <span className="block text-sm font-600 text-zinc-100">Quiet hours</span>
                <span className="block text-xs text-zinc-400">Post-tour reminders landing in this window wait until it ends. Celebrations always arrive at 8 AM.</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pl-7 text-xs text-zinc-400">
              <span>From</span>
              <HourSelect value={prefs.quiet_start} onChange={(v) => savePrefs({ ...prefs, quiet_start: v })} testid="quiet-start-select" />
              <span>to</span>
              <HourSelect value={prefs.quiet_end} onChange={(v) => savePrefs({ ...prefs, quiet_end: v })} testid="quiet-end-select" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={sendTest}
              disabled={testing}
              data-testid="push-test-btn"
              className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
            >
              <Send className="mr-1.5 h-4 w-4" /> {testing ? "Sending…" : "Send test notification"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={disable}
              disabled={busy}
              data-testid="push-disable-btn"
              className="border-zinc-700 bg-transparent text-zinc-400 hover:bg-zinc-800"
            >
              Turn off on this device
            </Button>
          </div>
        </>
      ) : (
        <Button
          onClick={enable}
          disabled={busy}
          data-testid="push-enable-btn"
          className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]"
        >
          <Bell className="mr-1.5 h-4 w-4" /> {busy ? "Turning on…" : "Turn on push notifications"}
        </Button>
      )}
      <p className="text-xs text-zinc-500">
        On iPhone, add Stage Door to your Home Screen and open it from there first.
      </p>
    </div>
  );
}
